// The toast: what just happened, said once at the bottom of the window. It
// sits on the inverse of the page (the text ink as its surface), so it is
// seen without a shadow, and leads with a mark that says how it went before
// a word is read: done, done with something to look at, or not done.

import { CloseButton, Text } from '@mantine/core';
import { notifications, type NotificationData } from '@mantine/notifications';
import { IconAlertTriangle, IconCheck, IconExclamationMark } from '@tabler/icons-react';

type Tone = 'done' | 'warning' | 'failed';

const toneOf = (color: NotificationData['color']): Tone => (color === 'danger' || color === 'red' ? 'failed' : color === 'warning' ? 'warning' : 'done');

const MARK = {
  done: <IconCheck size={16} stroke={2.5} />,
  warning: <IconAlertTriangle size={16} stroke={2} />,
  failed: <IconExclamationMark size={16} stroke={2.5} />,
} as const;

export function Toast({ data }: { data: NotificationData }) {
  const tone = toneOf(data.color);
  return (
    // A failure interrupts a screen reader; anything else waits its turn.
    <div className="toast" data-tone={tone} role={tone === 'failed' ? 'alert' : 'status'}>
      <span className="toast-mark" aria-hidden="true">{MARK[tone]}</span>
      <div className="toast-words">
        {data.title && <Text fw={600} fz={15} lh={1.35}>{data.title}</Text>}
        <Text fz={15} lh={1.4} fw={data.title ? 400 : 500}>{data.message}</Text>
      </div>
      <CloseButton className="toast-close" size={28} aria-label="Dismiss" onClick={() => { if (data.id) notifications.hide(data.id); }} />
    </div>
  );
}
